// Client-side helper that invokes the paystack-checkout Edge function
// and either:
//   - redirects the user to the returned authorization_url, or
//   - calls onNotConfigured() when the integration is not yet live
//     (PAYSTACK_SECRET_KEY missing on the deployed Edge fn).
//
// Auth gate: if there's no signed-in session, the caller should redirect
// to /auth?intent=signup&plan=<plan_code>. This helper returns the
// "needs_auth" result for that case rather than handling the redirect
// itself, so the caller controls navigation via React Router.
//
// Doctrine: never fabricate a successful redirect. The 503 path must be
// surfaced honestly to the user.

import { getBrowserClient } from "../supabase/browser.ts";

export type CheckoutResult =
  | { kind: "redirect"; authorization_url: string; reference: string }
  | { kind: "needs_auth" }
  | { kind: "not_configured"; message: string }
  | { kind: "error"; message: string };

export interface CheckoutOptions {
  /** Phase JB.3c — pass discount_pct=30 when redeeming a founder offer. */
  discount_pct?: number;
}

export async function initiatePaystackCheckout(
  planCode: string,
  options: CheckoutOptions = {},
): Promise<CheckoutResult> {
  const supabase = getBrowserClient();
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) return { kind: "needs_auth" };

  try {
    const body: Record<string, unknown> = { plan_code: planCode };
    if (typeof options.discount_pct === "number") {
      body.discount_pct = options.discount_pct;
    }
    const { data, error } = await supabase.functions.invoke<
      | { ok: true; authorization_url: string; reference: string; access_code: string | null }
      | { ok: false; error: string; message?: string }
    >("paystack-checkout", {
      body,
    });

    if (error) {
      // supabase-js wraps non-2xx responses into FunctionsHttpError.
      // The 503 "payments_not_configured" path lands here.
      const ctx = (error as { context?: Response }).context;
      if (ctx?.status === 503) {
        try {
          const body = (await ctx.clone().json()) as { error?: string; message?: string };
          if (body?.error === "payments_not_configured") {
            return {
              kind: "not_configured",
              message: body.message ??
                "Subscription checkout is launching soon — sign up for early access.",
            };
          }
        } catch {
          /* fall through to generic error */
        }
      }
      return { kind: "error", message: error.message ?? "Checkout failed" };
    }

    if (!data || data.ok !== true) {
      const message = data && "message" in data && typeof data.message === "string"
        ? data.message
        : "Checkout failed";
      if (data && "error" in data && data.error === "payments_not_configured") {
        return { kind: "not_configured", message };
      }
      return { kind: "error", message };
    }

    return {
      kind: "redirect",
      authorization_url: data.authorization_url,
      reference: data.reference,
    };
  } catch (err) {
    return {
      kind: "error",
      message: err instanceof Error ? err.message : "Checkout failed",
    };
  }
}
